
import { defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  voucherMethodsValues,
  voucherTypeValues,
  reusePreviousCouponValues,
} from '@/composables/flows';
import type {
  ResolvedReusedVoucher,
  ResolvedParentVoucherConfig,
  MultiVoucherConfigItem,
} from '@/composables/flows';
import { getOperatorById } from '@/components/flows/operators/registry';

const VALIDITY_MAP: Record<number, string> = {
  1: 'one_day', 2: 'two_day', 3: 'three_day', 4: 'four_day',
  5: 'five_day', 6: 'six_day', 7: 'one_week', 14: 'two_week',
  21: 'three_week', 28: 'four_week', 35: 'five_week', 42: 'six_week',
  49: 'seven_week', 56: 'eight_week', 70: 'ten_week', 84: 'twelve_week',
  98: 'fourteen_week', 112: 'sixteen_week', 126: 'eighteen_week',
  140: 'twenty_week', 154: 'twenty_two_week', 168: 'twenty_four_week',
  182: 'twenty_six_week',
};

export default defineComponent({
  name: 'ReusedVoucherPreview',

  props: {
    resolvedVoucher: {
      type: Object as PropType<ResolvedReusedVoucher>,
      required: true,
      default: () => ({ configs: [], hasMultiplePaths: false, notFound: true }),
    },
  },

  setup(props) {
    const { t } = useI18n();

    const METHODS = voucherMethodsValues;
    const TYPES = voucherTypeValues;

    const UNIT_LABELS: Record<string, string> = {
      DAY: 'automatedScenarios.actions.sending_channel.voucher_preview.expiring_unit_day',
      WEEK: 'automatedScenarios.actions.sending_channel.voucher_preview.expiring_unit_week',
      MONTH: 'automatedScenarios.actions.sending_channel.voucher_preview.expiring_unit_month',
    };

    const getExpiringPeriodLabel = (timePeriod: { value: number; unit: string }): string => {
      const unitKey = UNIT_LABELS[timePeriod.unit];
      if (unitKey) {
        return `${timePeriod.value} ${t(unitKey, timePeriod.value)}`;
      }
      return `${timePeriod.value} ${timePeriod.unit.toLowerCase()}(s)`;
    };

    const getStepLabel = (resolved: ResolvedParentVoucherConfig): string => {
      if (resolved.sourceLabel) return resolved.sourceLabel;
      const operator = getOperatorById(resolved.sourceStepType);
      if (operator) {
        return t(operator.label);
      }
      return resolved.sourceStepType;
    };

    const getDisplayConfigs = (resolved: ResolvedParentVoucherConfig): Record<string, MultiVoucherConfigItem> => {
      const result: Record<string, MultiVoucherConfigItem> = {};
      for (const [templateId, config] of Object.entries(resolved.voucherConfigs)) {
        if (!config.reuse || config.reuse === reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER) {
          result[templateId] = config;
        }
      }
      return result;
    };

    const getMethodLabel = (config: MultiVoucherConfigItem): string => {
      if (config.method === METHODS.AUTOMATIC) {
        return t('automatedScenarios.actions.sending_channel.vouchers_config.method.options.automatic');
      }
      if (config.method === METHODS.MANUAL) {
        return t('automatedScenarios.actions.sending_channel.vouchers_config.method.options.manual');
      }
      return '-';
    };

    const getTypeLabel = (config: MultiVoucherConfigItem): string => {
      switch (config.type) {
        case TYPES.PERCENTAGE:
          return t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.percentage');
        case TYPES.AMOUNT:
          return t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.amount');
        case TYPES.SHIPPING_FEES:
          return t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.shipping_fees');
        case TYPES.DUPLICATED:
          return t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.duplicated');
        default:
          return '-';
      }
    };

    const getAmountDisplay = (config: MultiVoucherConfigItem): string | null => {
      if (config.type === TYPES.PERCENTAGE) {
        return `${config.amount}%`;
      }
      if (config.type === TYPES.AMOUNT) {
        return `${config.amount}`;
      }
      return null;
    };

    const getValidityLabel = (config: MultiVoucherConfigItem): string | null => {
      const days = config.validtime || 0;
      if (days === 0) return null;
      const key = VALIDITY_MAP[days];
      if (key) {
        return t(`automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.${key}`);
      }
      return `${days} day(s)`;
    };

    return {
      t,
      METHODS,
      TYPES,
      getStepLabel,
      getDisplayConfigs,
      getMethodLabel,
      getTypeLabel,
      getAmountDisplay,
      getValidityLabel,
      getExpiringPeriodLabel,
    };
  },
});
